import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const DURATION = 5000;

const TYPES = {
  success: {
    icon: CheckCircle2,
    gradient: 'from-emerald-500 to-teal-500',
    bg: 'bg-white',
    border: 'border-emerald-200',
    iconColor: 'text-emerald-500',
    titleColor: 'text-emerald-700',
    textColor: 'text-gray-600',
    progress: 'bg-emerald-400',
    badge: 'bg-emerald-50 text-emerald-600',
    label: 'Success',
  },
  error: {
    icon: XCircle,
    gradient: 'from-rose-500 to-red-500',
    bg: 'bg-white',
    border: 'border-rose-200',
    iconColor: 'text-rose-500',
    titleColor: 'text-rose-700',
    textColor: 'text-gray-600',
    progress: 'bg-rose-400',
    badge: 'bg-rose-50 text-rose-600',
    label: 'Error',
  },
  warning: {
    icon: AlertTriangle,
    gradient: 'from-amber-400 to-orange-500',
    bg: 'bg-white',
    border: 'border-amber-200',
    iconColor: 'text-amber-500',
    titleColor: 'text-amber-700',
    textColor: 'text-gray-600',
    progress: 'bg-amber-400',
    badge: 'bg-amber-50 text-amber-600',
    label: 'Warning',
  },
  info: {
    icon: Info,
    gradient: 'from-blue-500 to-indigo-500',
    bg: 'bg-white',
    border: 'border-blue-200',
    iconColor: 'text-blue-500',
    titleColor: 'text-blue-700',
    textColor: 'text-gray-600',
    progress: 'bg-blue-400',
    badge: 'bg-blue-50 text-blue-600',
    label: 'Info',
  },
};

export default function Toast({ message, type = 'success', onClose }) {
  const [progress, setProgress] = useState(100);
  const [visible, setVisible] = useState(false);

  const config = TYPES[type] || TYPES.success;
  const Icon = config.icon;

  // Slide in on mount
  useEffect(() => {
    const show = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(show);
  }, []);

  // Progress bar countdown
  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / DURATION) * 100);
      setProgress(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 50);

    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 300);
    }, DURATION);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [onClose]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`fixed bottom-5 right-5 z-[100] w-[340px] rounded-2xl shadow-2xl border ${config.bg} ${config.border} overflow-hidden
        transition-all duration-300 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      style={{ willChange: 'transform, opacity' }}
    >
      {/* Top accent stripe */}
      <div className={`h-1 w-full bg-gradient-to-r ${config.gradient}`} />

      {/* Body */}
      <div className="p-4 flex items-start gap-3">
        {/* Icon circle */}
        <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${config.badge}`}>
          <Icon className={`w-5 h-5 ${config.iconColor}`} />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${config.titleColor}`}>
            {config.label}
          </p>
          <p className={`text-sm leading-snug font-medium ${config.textColor} break-words`}>
            {message}
          </p>
        </div>

        {/* Close */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors mt-0.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 w-full bg-gray-100">
        <div
          className={`h-full ${config.progress} transition-all duration-75 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
